import { useMutation } from '@apollo/client';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  actionSetOperatorIsLoggingIn,
  actionSetRecentOperatorRuns,
} from 'lib/actions';
import {
  SUBMIT_LABOR_TICKET_TRANSACTION,
  START_ACTIVITY_WITH_LABOR_TICKET_NATS,
} from 'lib/api/mutations';
import useHandleNatsLaborTicketResponse from 'lib/hooks/useHandleNatsLaborTicketResponse';
import { throwCriticalErrorToast } from 'lib/utils/toast';
import { getCurrentWorkOrderOp } from 'lib/selectors/getCurrentWorkOrderOp';
import { getLatestActivityMode } from 'lib/selectors/getLatestActivityMode';
import { getMachine } from 'lib/selectors/getMachine';
import { getOperatorIsLoggingIn } from 'lib/selectors/getOperatorIsLoggingIn';
import { getOperators } from 'lib/selectors/getOperators';
import { getPostLaborUpdatesOnNats } from 'lib/selectors/getPostLaborUpdatesOnNats';
import { getEmbeddedLaborTicketFlowEnabled } from 'lib/selectors/getEmbeddedLaborTicketFlowEnabled';

/**
 * Custom hook that provides functionality to start an operator session with an associated labor ticket.
 * This hook handles the creation of both an operator run and a corresponding labor ticket in a single transaction.
 *
 * @returns {Function} A function that starts an operator with labor ticket creation
 * @example
 * const startOperatorWithLaborTicket = useStartOperatorWithLaborTicket();
 * startOperatorWithLaborTicket({
 *   operatorId: 'op-123',
 *   startAt: '2023-09-17T10:00:00Z'
 * });
 */
const useStartOperatorWithLaborTicket = () => {
  const dispatch = useDispatch();
  const handleNatsLaborTicketResponse = useHandleNatsLaborTicketResponse();
  const isLoggingIn = useSelector(getOperatorIsLoggingIn);
  const currentWorkOrderOp = useSelector(getCurrentWorkOrderOp);
  const machine = useSelector(getMachine);
  const latestActivityMode = useSelector(getLatestActivityMode);
  const operators = useSelector(getOperators);
  const natsFlowEnabled = useSelector(getPostLaborUpdatesOnNats);
  const isEmbeddedLaborFlowEnabled = useSelector(
    getEmbeddedLaborTicketFlowEnabled
  );

  // NATS flow: Submit labor ticket via NATS endpoint
  const [requestSubmitLaborTicketNats, { loading: loadingNats }] = useMutation(
    START_ACTIVITY_WITH_LABOR_TICKET_NATS,
    {
      fetchPolicy: 'no-cache',
      onCompleted: async ({ startActivityWithLaborTicketResponse }) => {
        if (startActivityWithLaborTicketResponse?.success === false) {
          const errorMessage =
            startActivityWithLaborTicketResponse?.error ||
            startActivityWithLaborTicketResponse?.message ||
            'Failed to start operator with labor ticket';
          throwCriticalErrorToast(errorMessage);
          return;
        }

        await handleNatsLaborTicketResponse({
          response: startActivityWithLaborTicketResponse,
          options: { handleOperatorRun: true },
        });

        const { erpResponses } = startActivityWithLaborTicketResponse;
        if (erpResponses && Array.isArray(erpResponses)) {
          const erpErrors = erpResponses
            .filter((resp) => {
              return resp.error || resp.response?.status === 'error';
            })
            .map((resp) => {
              return resp.error || resp.response?.error?.message;
            })
            .filter(Boolean);

          erpErrors.forEach((error) => {
            throwCriticalErrorToast(`ERP Error: ${error}`);
          });
        }
      },
      onError: (error) => {
        throwCriticalErrorToast(
          `Could not start operator with labor ticket: ${error.message}`
        );
      },
    }
  );

  // Standard flow: Submit labor ticket transaction that also starts an operator run
  const [
    requestSubmitLaborTicketTransaction,
    { loading: loadingStandard },
  ] = useMutation(SUBMIT_LABOR_TICKET_TRANSACTION, {
    fetchPolicy: 'no-cache',
    onCompleted: async ({ submitLaborTicketTransactionResponse }) => {
      if (submitLaborTicketTransactionResponse?.startOperatorRun) {
        const { startOperatorRun } = submitLaborTicketTransactionResponse;
        const { operator, ...operatorRun } = startOperatorRun;

        // Update store with the newly started operator run
        const mappedPayload = {
          operatorRuns: [
            {
              ...operatorRun,
              machineId: machine?.id,
              operatorId: operator?.id,
            },
          ],
        };
        dispatch(actionSetRecentOperatorRuns(mappedPayload));
      }
    },
  });

  const loading =
    natsFlowEnabled && isEmbeddedLaborFlowEnabled
      ? loadingNats
      : loadingStandard;

  // Sync loading state with global operator login state
  useEffect(() => {
    dispatch(actionSetOperatorIsLoggingIn(loading));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  /**
   * Starts an operator session and creates an associated labor ticket in a single transaction.
   *
   * @param {Object} startOperatorRun - The operator run configuration
   * @param {string} startOperatorRun.operatorId - The ID of the operator to start
   * @param {string} [startOperatorRun.startAt] - ISO timestamp for when the operator run starts
   * @param {string} [startOperatorRun.startAtOverride] - Override timestamp that takes precedence over startAt
   * @param {boolean} [startOperatorRun.pinToPreviousEvent] - Whether to pin this run to the previous event
   * @returns {Promise<Object>|null} The mutation result or null if already logging in
   */
  return async (startOperatorRun) => {
    // Prevent multiple simultaneous login attempts
    if (isLoggingIn || loading) {
      return null;
    }

    // Get ERP person ID from operator data
    const personId = operators.find((op) => {
      return op.id === startOperatorRun.operatorId;
    })?.erpId;

    // Build labor ticket with current work order operation details
    const createLaborTicket = {
      clockIn: startOperatorRun.startAtOverride || startOperatorRun.startAt,
      clockOut: null,
      lot: currentWorkOrderOp.lot,
      sequenceNumber: currentWorkOrderOp.sequenceNumber,
      split: currentWorkOrderOp.split,
      sub: currentWorkOrderOp.sub,
      state: 'OPEN',
      resourceId: machine.erpResourceId,
      personId,
      workOrderId: currentWorkOrderOp.workOrderId,
      type: latestActivityMode,
      goodParts: 0,
      badParts: 0,
    };

    // NATS flow: use submit-nats endpoint
    if (natsFlowEnabled && isEmbeddedLaborFlowEnabled) {
      return requestSubmitLaborTicketNats({
        variables: {
          input: {
            startOperatorRun,
            createLaborTicket,
          },
        },
      });
    }

    // Standard flow: Execute the combined transaction
    return requestSubmitLaborTicketTransaction({
      variables: {
        input: {
          startOperatorRun,
          createLaborTicket,
        },
      },
    });
  };
};

export default useStartOperatorWithLaborTicket;
